import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import Base from '../base'
import ClipLoader from 'react-spinners/ClipLoader'
import * as amplitude from '@amplitude/analytics-browser'
import { useObject } from 'react-firebase-hooks/database'
import { ref, database, auth } from '../../firebase'
import { useNavigate, useParams } from 'react-router-dom'
import Joyride, { ACTIONS } from 'react-joyride'
import CanvaModal from '../../components/EventDetails/CanvaModal/CanvaModal'
import EventCarouselView from '../../components/EventCarouselView'
import EventDetailsView from '../../components/EventDetailsView'
import { Helmet } from 'react-helmet'
import RequestTab from './Tab/RequestTab/RequestTab'
import MusicTab from './Tab/MusicTab/MusicTab'
import { useAuthState } from 'react-firebase-hooks/auth'
import EventDetailsViewCreatedEvent from '../../components/EventDetailsView/EventDetailsViewCreatedEvent/EventDetailsViewCreatedEvent'
import { getSettingsData, setSettingsData } from '../../services/database'
import { eventTimePassed } from '../../helpers'
import Button from '../../components/Button/Button'
import EventDetailsViewInviteDrawer from '../../components/EventDetailsView/EventDetailsViewInviteDrawer/EventDetailsViewInviteDrawer'
import { Spinner, useToast } from '@chakra-ui/react'
import { getTokens } from '../../services/helpers'
import { createEventPlaylist } from '../../services/Circle'
import { ReactComponent as PremiumEventStar } from '../../assets/icons/premium-event-start.svg'
import { update } from 'firebase/database'

const EventDetails = () => {
  const { circleCode } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [user, loading] = useAuthState(auth)
  const [toggleTab, setToggleTab] = useState(0)
  const [canvaModal, setCanvaModal] = useState(false)
  const [firstTimeModal, setFirstTimeModal] = useState(false)
  const [isLoadingItemsList, setLoadingItemsList] = useState(false)
  const [isLoadingPlaylist, setLoadingPlaylist] = useState(false)
  const [run, setRun] = useState(false)

  const [inviteModal, setInviteModal] = useState(false)

  const [eventSnap, eventLoading] = useObject(
    ref(database, `circles/${circleCode || '1'}/info`)
  )
  const [guestsSnap, guestsLoading] = useObject(
    ref(database, `circles/${circleCode || '1'}/guests`)
  )
  const [playlistSnap, playlistLoading] = useObject(
    ref(database, `circles/${circleCode || '1'}/playlist`)
  )
  const [itemsSnap, itemsLoading] = useObject(
    ref(database, `circles/${circleCode || '1'}/items`)
  )
  const [featureFlagsSnap] = useObject(
    ref(database, 'superBowl/featureFlags')
  )

  const playlist = playlistSnap?.val()
  const itemsInfo = itemsSnap?.val()
  const guestsInfo = useMemo(() => guestsSnap?.val() || {}, [guestsSnap])
  const eventInfo = eventSnap?.val()
  const squaresEnabled = featureFlagsSnap?.val()?.squares_enabled === true
  const isTimePassed = !!eventInfo?.isPremium && eventTimePassed(eventInfo)

  const settingsData = getSettingsData() ?? {}

  const steps = [
    {
      content: (
        <div className="flex flex-col gap-3">
          <h2>You created an event—nice!</h2>
          <span>Here's a quick tour of your Event page.</span>
        </div>
      ),
      placement: 'center',
      target: 'body',
      disableBeacon: true
    },
    {
      target: '.my-first-step',
      content: 'Need to edit your event? Click here.',
      disableBeacon: true,
      placement: 'auto'
    },
    {
      target: '.my-second-step',
      content: 'Explore optional event features you might want to add.',
      disableBeacon: true,
      placement: 'auto'
    },
    {
      target: '.my-third-step',
      content:
        'Click here to view an example of how an invitation appears to guests.',
      disableBeacon: true,
      placement: 'auto'
    },
    {
      target: '.my-thrid-step',
      content: (
        <div className="flex flex-col gap-3">
          <h2>Ready to send invites?</h2>
          <span>Click here to continue.</span>
        </div>
      ),
      disableBeacon: true,
      locale: {
        last: 'Close'
      },
      placement: 'auto'
    }
  ]

  const handleSetJoyrideStorage = useCallback(() => {
    const joyrideAlreadyRan = localStorage.getItem(`joyride-${circleCode}`)

    if (joyrideAlreadyRan === 'true') {
      return
    }

    localStorage.setItem(`joyride-${circleCode}`, 'true')

    setRun(true)
  }, [circleCode])

  useEffect(() => {
    if (!user && !loading) {
      navigate(`/login?redirect_url=${window.location.pathname}`, {
        replace: true
      })
      return
    }
    if (!eventLoading && !loading) {
      const isHost = eventInfo?.hostID === user?.uid
      if (!eventInfo || !isHost) {
        navigate(`/404?from=${window.location.pathname}`, { replace: true })
        return
      }
      // if (eventInfo.withCanva && !eventInfo.completed) {
      //   navigate(`/event/update/${circleCode}/info`, { replace: true })
      //   return
      // }
    }
  }, [eventLoading, loading, user, eventInfo, navigate, circleCode])

  const handleCreatePlaylist = async () => {
    setLoadingPlaylist(true)
    try {
      if (!eventInfo.playlistID) {
        const token = await getTokens('guest', null)
        if (!token.accessToken) return

        amplitude.track('Music Playlist added', {
          circleCode,
          status: 'success'
        })

        await createEventPlaylist({
          token,
          settingsToken: token,
          option: 'guest',
          provider: 'spotify',
          circleCode,
          name: eventInfo.name,
          type: eventInfo.type
        })
      }

      await update(ref(database, `circles/${circleCode}/info`), {
        musicCollab: true
      })
      setToggleTab(1)
    } catch (err) {
      console.log('fail', err)
      toast({
        status: 'error',
        title: err.message || 'Error',
        position: 'top'
      })
      if (!eventInfo.playlistID) {
        amplitude.track('Music Playlist added', {
          circleCode,
          status: 'failure'
        })
      }
    } finally {
      setLoadingPlaylist(false)
    }
  }

  const handleCreateItemList = async () => {
    setLoadingItemsList(true)
    try {
      await update(ref(database, `circles/${circleCode}/info`), {
        extraCollab: true
      })
    } finally {
      setToggleTab(0)
      setLoadingItemsList(false)
    }
  }

  const previewInvitation = () => {
    localStorage.setItem(`preview`, JSON.stringify(circleCode))
    navigate(`/event/${circleCode}/preview`)
  }

  useEffect(() => {
    if (!itemsLoading && !eventLoading) {
      if (
        !!eventInfo?.musicCollab &&
        Object.keys(itemsInfo || {})?.length === 0
      ) {
        setToggleTab(1)
      }
    }
  }, [eventInfo?.musicCollab, itemsInfo, itemsLoading, eventLoading])

  useEffect(() => {
    if (!!eventInfo && !eventLoading) {
      if (!settingsData.firstTimeEvent) {
        setFirstTimeModal(true)
      } else {
        handleSetJoyrideStorage()
      }
    }
  }, [
    eventInfo,
    eventLoading,
    settingsData.firstTimeEvent,
    handleSetJoyrideStorage
  ])

  if (
    loading ||
    eventLoading ||
    guestsLoading ||
    playlistLoading ||
    itemsLoading
  ) {
    return (
      <Base
        hideBg={true}
        bottomBar={false}
        allowFullHeight={true}
        verifiedOnly={true}
        headerBackgroundColor={'transparent'}
        whiteLogo
        whiteTheme
      >
        <Helmet>
          <meta charSet="utf-8" />
          <title>Coteri - Online invitation details</title>
          <meta
            name="description"
            content="See and edit the details for your invitation. Track responses. See the music playlist. Copy the invite link. Anyone with the invite link can add songs to the music playlist."
          />
        </Helmet>
        <div className="flex flex-1 items-center justify-center">
          <ClipLoader color="#5B4ABC" />
        </div>
      </Base>
    )
  }

  return (
    <React.Fragment>
      <EventDetailsViewInviteDrawer
        isOpen={inviteModal}
        onClose={() => setInviteModal(false)}
        eventInfo={eventInfo}
        circleCode={circleCode}
      />

      <Base
        hideBg={true}
        bottomBar={false}
        allowFullHeight={true}
        verifiedOnly={true}
        whiteLogo
        whiteTheme
        headerBackgroundColor={'transparent'}
      >
        <Helmet>
          <meta charSet="utf-8" />
          <title>{`Coteri${!!eventInfo?.name ? ` - ${eventInfo.name}` : ''}`}</title>
          <meta
            name="description"
            content="See and edit the details for your invitation. Track responses. See the music playlist. Copy the invite link. Anyone with the invite link can add songs to the music playlist."
          />
        </Helmet>

        {eventLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <ClipLoader color="#5B4ABC" />
          </div>
        ) : (
          <div className="flex flex-1 items-center h-full flex-col mb-[50px] relative">
            {!eventInfo?.date ? (
              <div
                className="w-full p-4 mb-4 text-md text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400"
                role="alert"
              >
                <div className="font-bold">Inactive Event!</div>
                <div>Add a date/time to activate this event.</div>
              </div>
            ) : isTimePassed ? (
              <div
                className="w-full p-4 mb-4 text-md text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400"
                role="alert"
              >
                <div className="font-bold">Event Closed</div>
                <div>
                  This event has ended. You can no longer edit the event
                  details.
                </div>
              </div>
            ) : !!eventInfo?.isPremium ? (
              <div className="flex items-end self-end justify-center">
                <span
                  className="mr-2 font-bold text-lg"
                  style={{
                    background:
                      'linear-gradient(to right, #FF00D9, #F200CE, #E600C3)',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent'
                  }}
                >
                  Premium event
                </span>

                <PremiumEventStar width={36} height={36} />
              </div>
            ) : null}
            <div className="w-full flex gap-5 flex-col sm:flex-row">
              <div className="order-1 sm:w-[42%] w-[100%] relative">
                <EventCarouselView eventInfo={eventInfo} removeEdit />
              </div>
              <div className="order-2 flex-1">
                <EventDetailsView
                  circleCode={circleCode}
                  eventInfo={eventInfo}
                  guestsInfo={guestsInfo}
                  hideUndefineFields
                />

                {isTimePassed &&
                !eventInfo.noteForGuests ? null : !eventInfo.noteForGuests ? (
                  <></>
                ) : (
                  !eventInfo.noteForGuests && (
                    <div className="mt-[12px] w-full shadow-purple rounded-md">
                      <div className="flex flex-row items-center justify-start">
                        <div className="bg-off-white p-3 w-full flex flex-col justify-center rounded-md">
                          <span
                            className="ml-[8px] text-primary font-semibold hover:underline cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/event/${circleCode}/edit?description=true`
                              )
                            }
                          >
                            + Add event description, or a note for your guests
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

            {isTimePassed && !eventInfo.noteForGuests
              ? null
              : eventInfo.noteForGuests && (
                  <div className="w-full mt-4">
                    <div className="flex items-center justify-between">
                      <span>Additional event information</span>
                    </div>
                    <div className="w-full h-[2px] bg-secondary mb-2" />
                    <button
                      className="bg-off-white p-3 flex flex-col justify-center rounded-md cursor-pointer w-full"
                      disabled={isTimePassed}
                      onClick={() => navigate(`/event/${circleCode}/edit`)}
                    >
                      <span>{eventInfo.noteForGuests}</span>
                    </button>
                  </div>
                )}

            <div className="mt-4 w-full flex justify-center">
              {!isTimePassed ? (
                <div className="flex items-center justify-between max-w-[350px] w-full my-thrid-step">
                  <Button
                    text="Start inviting"
                    size={'medium'}
                    onClick={() => {
                      amplitude.track('Send Invite Clicked', {
                        event_id: circleCode
                      })
                      setInviteModal(true)
                    }}
                    className={'w-full'}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>

            <div className="w-full mt-2">
              <span
                className="text-[13px] text-black text-start my-third-step"
                onClick={previewInvitation}
              >
                Want to see what your guests see?{' '}
                <span className="underline text-secondary cursor-pointer">
                  See an example
                </span>
              </span>

              <div className="my-second-step mt-4">
                {isTimePassed & !eventInfo?.extraCollab &&
                !eventInfo?.musicCollab ? null : (
                  <div className="flex flex-1 flex-col">
                    <div className="w-full flex items-center justify-center sm:mb-0 mb-2">
                      <div className="flex flex-1 bg-off-white h-[2px] mr-2" />
                      <span className="text-[13px] text-secondary">
                        Additional event features
                      </span>
                      <div className="flex flex-1 bg-off-white h-[2px] ml-2" />
                    </div>
                  </div>
                )}

                {/* FEATURE: Super Bowl */}
                {squaresEnabled && (
                  <div
                    className={`cursor-pointer relative flex flex-1 py-[10px] items-center justify-center rounded-md bg-off-white text-primary transition-colors duration-300 ease-in-out hover:bg-[#e6e6f1] mb-2`}
                    onClick={() => {
                      if (!!eventInfo?.squaresGameId) {
                        navigate(`/event/${circleCode}/squares/summary`)
                        return
                      }
                      navigate(`/event/${circleCode}/squares/choose`)
                    }}
                  >
                    <span>
                      {!!eventInfo?.squaresGameId ? '' : '+'} Super Bowl Squares
                    </span>
                  </div>
                )}

                {isTimePassed & !eventInfo?.extraCollab &&
                !eventInfo?.musicCollab ? null : (
                  <ul
                    className={`flex w-full mb-5 sm:gap-2 gap-2 flex-col mt-1 sm:flex-row`}
                  >
                    {isTimePassed & !eventInfo?.extraCollab ? null : (
                      <button
                        onClick={() =>
                          eventInfo?.extraCollab
                            ? setToggleTab(0)
                            : handleCreateItemList()
                        }
                        disabled={isLoadingItemsList}
                        className={`cursor-pointer relative flex flex-1 py-[10px] items-center justify-center rounded-md disabled:cursor-default ${toggleTab === 0 && eventInfo?.extraCollab ? 'bg-terciary text-white' : 'bg-off-white text-primary transition-colors duration-300 ease-in-out hover:bg-[#e6e6f1] disabled:bg-off-white'}`}
                      >
                        {isLoadingItemsList ? (
                          <Spinner />
                        ) : (
                          <span>
                            {eventInfo?.extraCollab
                              ? 'What to bring list'
                              : '+ Add “What to bring” list'}
                          </span>
                        )}
                      </button>
                    )}

                    {isTimePassed & !eventInfo?.musicCollab ? null : (
                      <button
                        onClick={() =>
                          eventInfo?.musicCollab
                            ? setToggleTab(1)
                            : handleCreatePlaylist()
                        }
                        disabled={isLoadingPlaylist}
                        className={`cursor-pointer relative flex flex-1 py-[10px] items-center justify-center rounded-md disabled:cursor-default ${toggleTab === 1 && eventInfo?.musicCollab ? 'bg-terciary text-white' : 'bg-off-white text-primary transition-colors duration-300 ease-in-out hover:bg-[#e6e6f1] disabled:bg-off-white'}`}
                      >
                        {isLoadingPlaylist ? (
                          <Spinner />
                        ) : eventInfo?.musicCollab ? (
                          'Music playlist'
                        ) : (
                          '+ Add Music playlist'
                        )}
                      </button>
                    )}
                  </ul>
                )}

                {eventInfo?.extraCollab && (
                  <RequestTab
                    circleInfo={eventInfo}
                    isToggled={toggleTab === 0}
                    itemsInfo={itemsInfo}
                    eventInfo={eventInfo}
                    guestsInfo={guestsInfo}
                    owner={true}
                  />
                )}

                {eventInfo?.musicCollab && (
                  <MusicTab
                    isToggled={toggleTab === 1}
                    circleInfo={eventInfo}
                    playlist={playlist}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {!!canvaModal ? (
          <CanvaModal
            circleCode={circleCode}
            isOpen={canvaModal}
            handleOnClose={() => setCanvaModal(false)}
            oldOcassion={eventInfo.ocassion}
          />
        ) : null}

        {!!firstTimeModal ? (
          <EventDetailsViewCreatedEvent
            isOpen={firstTimeModal}
            onClose={() => {
              setFirstTimeModal(false)
              setSettingsData({ ...settingsData, firstTimeEvent: true })
              handleSetJoyrideStorage()
            }}
          />
        ) : null}
      </Base>

      <Joyride
        steps={steps}
        run={run}
        continuous
        callback={(data) => {
          const { action } = data

          if (action === ACTIONS.CLOSE && run) {
            setRun(false)
          }
        }}
        styles={{
          buttonNext: {
            paddingRight: 30,
            paddingLeft: 30,
            paddingTop: 14,
            paddingBottom: 14,
            borderRadius: 80,
            backgroundColor: '#5B4ABC'
          },
          buttonBack: {
            color: '#000'
          },
          options: {
            zIndex: 9999999
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)'
          }
        }}
      />
    </React.Fragment>
  )
}

export default memo(EventDetails)
